import { ACTIONS } from './connectionUtils'
import { getMessage } from './messageUtils'
import type { AlertTime } from './recipeUtils'

const DAYS_OF_WEEK = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
]

const MONTHS_OF_YEAR = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

const MEAL_TYPES = {
  desayuno: 0,
  almuerzo: 1,
  comida: 2,
  merienda: 3,
  cena: 4,
}

type MealType = 'desayuno' | 'almuerzo' | 'comida' | 'merienda' | 'cena'
type MealTypeTupper =
  | 'desayuno_from_tupper'
  | 'almuerzo_from_tupper'
  | 'comida_from_tupper'
  | 'merienda_from_tupper'
  | 'cena_from_tupper'

interface day {
  date: string | null
  desayuno: number | null
  almuerzo: number | null
  comida: number | null
  merienda: number | null
  cena: number | null
  desayuno_from_tupper: boolean
  almuerzo_from_tupper: boolean
  comida_from_tupper: boolean
  merienda_from_tupper: boolean
  cena_from_tupper: boolean
  desayuno_alert: string | null
  desayuno_alertTime: AlertTime | null
  almuerzo_alert: string | null
  almuerzo_alertTime: AlertTime | null
  comida_alert: string | null
  comida_alertTime: AlertTime | null
  merienda_alert: string | null
  merienda_alertTime: AlertTime | null
  cena_alert: string | null
  cena_alertTime: AlertTime | null
}

interface CalendarResponse {
  success: boolean
  days?: day[]
  error?: string
  empty?: boolean
}

function getFirstDayOfWeek(date: Date): Date {
  const firstDay = new Date(date)
  const day = date.getDay() || 7
  if (day !== 1) {
    firstDay.setHours(-24 * (day - 1))
  }
  firstDay.setHours(0, 0, 0, 0)
  return firstDay
}

function getLastDayOfWeek(date: Date): Date {
  const lastDay = new Date(date)
  const day = date.getDay() || 7
  if (day !== 7) {
    lastDay.setHours(24 * (7 - day))
  }
  lastDay.setHours(23, 59, 59, 999)
  return lastDay
}

function getNextWeekDay(lastDay: Date): Date {
  const nextWeekDay = new Date(lastDay)
  nextWeekDay.setDate(nextWeekDay.getDate() + 1)
  return nextWeekDay
}

function getPreviousWeekDay(firstDay: Date): Date {
  const previousWeekDay = new Date(firstDay)
  previousWeekDay.setDate(previousWeekDay.getDate() - 1)
  return previousWeekDay
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

async function getWeekInformation(
  firstDay: Date,
  lastDay: Date,
  createWeek: boolean,
  user: string
): Promise<CalendarResponse> {
  try {
    const response = await fetch('/api/calendar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.GET,
        firstDay: formatDate(firstDay),
        lastDay: formatDate(lastDay),
        createWeek,
        user,
      }),
    })
    const data: CalendarResponse = await response.json()
    return data
  } catch (error) {
    console.error('Calendar error:', error)
    throw error
  }
}

async function deleteMeal(day: string, meal: number, user: string) {
  try {
    await fetch('/api/calendar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.DELETE,
        day,
        meal,
        user,
      }),
    })
  } catch (error) {
    alert('Error al borrar la comida') // TODO: Better error handling
    console.error('Calendar error:', error)
    throw error
  }
}

async function insertMeal(
  day: string,
  meal: number,
  recipe: number,
  alertText: string | null,
  alertTime: AlertTime | null,
  user: string
) {
  try {
    await fetch('/api/calendar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.INSERT,
        day,
        meal,
        recipe,
        alert: alertText,
        alertTime,
        user,
      }),
    })
  } catch (error) {
    alert(getMessage('error_insert_meal'))
    console.error('Calendar error:', error)
    throw error
  }
}

async function updateMeal(
  day: string,
  meal: number,
  recipe: number,
  alertText: string | null,
  alertTime: AlertTime | null,
  user: string
) {
  try {
    await fetch('/api/calendar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: ACTIONS.UPDATE,
        day,
        meal,
        recipe,
        alert: alertText,
        alertTime,
        user,
      }),
    })
  } catch (error) {
    alert('Error al modificar la comida') // TODO: Better error handling
    console.error('Calendar error:', error)
    throw error
  }
}

export {
  DAYS_OF_WEEK,
  MONTHS_OF_YEAR,
  MEAL_TYPES,
  getFirstDayOfWeek,
  getLastDayOfWeek,
  getNextWeekDay,
  getPreviousWeekDay,
  getWeekInformation,
  deleteMeal,
  insertMeal,
  updateMeal,
}

export type { CalendarResponse, MealType, MealTypeTupper, day }
